/**
 * Textes de la page /v51 (OpenBroV51 = mise en page de la home), FR et EN.
 * Même ton que /v50 (openBroV50Copy.js) : phrases courtes, un mot mis en avant, uniquement des faits de l'offre
 * (config/offer.js, espace membre, lib/callSlots). Le texte de la home reste dans openBroHomeCopy.js.
 * `**mot**` = mot qui porte le pitch : rendu en blanc et plus lourd, le reste en gris.
 */

const COPY = {
  fr: {
    lang: 'FR',
    switchLabel: 'Switch to English',
    nav: [['#chemins', 'Pourquoi'], ['#appel', 'L’appel'], ['#bros', 'Les bros'], ['#campus', 'L’espace membre'], ['#prix', 'Prix'], ['#faq', 'FAQ']],
    login: 'Connexion',
    headerLogin: 'Se connecter',
    headerCta: 'Rejoindre',
    eyebrow: 'Mentorat privé avec Gaut Bonac',
    title: 'Un mentor.\nUn plan.\n**Dès aujourd’hui.**',
    lead: 'Chaque mois, **Gaut t’appelle.** Vous réglez ce qui te bloque, et tu repars avec **ton plan des 30 prochains jours.**',
    between: 'Entre deux appels : **6 campus** et **une communauté privée** pour le tenir.',
    cta: (price) => `Rejoindre pour ${price}/mois`,
    ctaShort: 'Rejoindre',
    secondary: 'Voir les deux chemins',
    reassure: ['Sans engagement', 'Résiliable à tout moment', 'Accès immédiat'],
    period: '/mois',

    cardKicker: 'Ton premier appel',
    cardTitle: 'Choisis ton créneau.',
    cardBody: 'Gaut t’appelle au numéro indiqué au paiement. Le créneau est bloqué dès que c’est réglé.',
    slotsLabel: 'Créneaux disponibles',
    bookCta: (label) => `Réserver ${label}`,
    bookFallback: 'Réserver mon appel',
    cardFoot: (price) => `Inclus dans l’abonnement à ${price}/mois`,
    // Toasts bas-gauche. `deal` vient de config/offer.js : `previous` / `discount` restent null tant qu'aucun
    // ancien prix réel n'y est saisi, et alors rien n'est barré. 4e valeur = prix barré affiché avant la ligne.
    toasts: [
      ({ slot }) => ['Ton appel', slot, 'Encore libre'],
      ({ price, perDay, previous, discount, saving }) => [
        'L’offre OpenBro',
        `${price}/mois`,
        discount ? `−${discount} · tu économises ${saving}/mois` : `Moins de ${perDay} par jour · sans engagement`,
        previous,
      ],
      () => ['Tout compris', '1 appel privé + 6 campus', 'Et la communauté, dès le paiement'],
    ],
    // Vrai membre récent (worker.js /api/recent-members) : prénom saisi par lui au paiement, ville, date.
    memberToast: ({ name, city }, ago) => ['Vient de rejoindre', city ? `${name} · ${city}` : name, ago],
    toastClose: 'Fermer',

    takeSlot: (label) => `Réserver ${label}`,
    headerSlot: (time) => `Réserver ${time}`,
    barSlot: 'Ton créneau',
    barTake: 'Je le prends',
    barUntil: (left) => `Réservable encore ${left}`,
    heroUrge: ({ label, left }) => `**Ton appel : ${label}.** Réservable encore **${left}**. Ensuite, il passe.`,
    leftFmt: { day: 'j', hour: 'h', min: 'min', sec: 's' },
    urge: {
      hero: ({ left }) => `**Il se réserve maintenant.** Encore ${left}, ensuite le créneau passe.`,
      truth: ({ label }) => `**Ton appel est prêt : ${label}.**`,
      stories: ({ label }) => `Un appel, puis un plan. **Le tien : ${label}.**`,
      paths: ({ left }) => `**Ton créneau se réserve encore ${left}.**`,
      time: ({ label }) => `**Ton prochain créneau : ${label}.**`,
      call: ({ left }) => `**Réservable encore ${left}.** Ensuite, il passe.`,
      wins: ({ label }) => `**Ton créneau t’attend : ${label}.**`,
      mentor: ({ label }) => `**Ça commence par un appel.** Le tien : ${label}.`,
      price: ({ left, price }) => `**${price}/mois.** Ton créneau se réserve encore ${left}.`,
      final: ({ label, left }) => `**${label}.** Réservable encore ${left}.`,
    },

    truthPill: 'Passe à l’action',
    truthTitle: 'L’école ne t’a pas appris **à avancer.**',
    truth: [
      'On t’a appris à réciter. **Pas à te lever tôt, à manger proprement ou à lancer un projet.**',
      'Les vidéos gratuites ne manquent pas. Ce qui manque, c’est **quelqu’un qui te dit quoi faire ce mois-ci**, et qui vérifie le mois suivant.',
      '**OpenBro, c’est ça** : un appel, un plan, et un cadre pour le tenir.',
    ],

    storiesTitle: 'Un appel. **Puis un plan.**',
    stories: { adam: ['La salle', '100 kg au squat'], ines: ['L’assiette', 'Meal prep le dimanche'], theo: ['Le business', 'Site en ligne en 30 jours'], lucas: ['La vie', 'Debout à 6 h 30'] },

    pathsPill: 'Choisis ton chemin',
    pathsTitle: 'Continuer seul **vs** OpenBro',
    aloneTitle: 'Continuer seul',
    alone: [
      'Des heures de vidéos, rien ne bouge',
      'Des conseils qui se contredisent',
      'Personne pour te répondre',
      'Tu manges ce qui traîne',
      'Tu t’entraînes sans plan',
      'Tu recommences lundi',
    ],
    aloneCta: 'Continuer seul',
    aloneHref: 'https://fr.wikipedia.org/wiki/Nomenclature_d%27activit%C3%A9s_fran%C3%A7aise',
    withTitle: 'OpenBro',
    with: [
      'Un appel privé avec Gaut chaque mois',
      'Ton plan 30 jours : alimentation, entraînement, business',
      'Les 6 campus et leurs leçons',
      'La communauté privée',
      'Le suivi de tes habitudes et le Monk Mode',
      'Sans engagement, résiliable à tout moment',
    ],

    timePill: 'La discipline',
    timeTitle: 'Avancer est **une compétence.**',
    time: [
      'Elle s’apprend comme le reste : **un objectif, des étapes, et quelqu’un qui te les redemande.**',
      '**Trente jours, c’est assez pour changer une routine.** À condition d’avoir un plan.',
    ],
    planPill: 'Ton plan',
    planTitle: 'Trois plans. **Une direction.**',
    plan: [
      '**Alimentation** : quoi manger, et quand.',
      '**Entraînement** : tes séances de la semaine, dans l’ordre.',
      '**Business** : les actions qui font avancer ton projet.',
      'On le construit **ensemble, pendant l’appel.**',
    ],
    planShot: 'Capture réelle : formation Nutrition & Performance',

    callPill: 'Le mentor',
    callTitle: 'Gaut,\n**au téléphone.**',
    callTitleSlot: (label) => `Ton appel.\n**${label}.**`,
    call: (minutes) => [
      `Tu choisis ton créneau **avant de payer**. Gaut t’appelle au numéro indiqué${minutes ? ` : **${minutes} min en tête-à-tête.**` : '.'}`,
      'Pas un cours enregistré. **Une conversation sur ta situation**, et tu repars avec tes priorités du mois.',
    ],
    callShot: 'Capture réelle : réservation sur mobile',

    formationsPill: 'Accès à 6 campus',
    formationsTitle: 'Ce que tu vas **apprendre**',
    formationsBody: 'Des leçons concrètes, de zéro à l’exécution. **Tu commences par ce qui compte pour toi.**',
    formations: {
      musculation: ['Musculation & Discipline', 'Technique, programmation, progression. De zéro à intermédiaire.'],
      nutrition: ['Nutrition & Performance', 'Quoi manger, quand, et un système simple à tenir.'],
      reseaux: ['E-commerce & Réseaux sociaux', 'Lancer ta marque, vendre en ligne, faire grandir un compte.'],
      startup: ['Startup', 'Ton idée trouve ici son associé. Un bro pour lancer le projet avec toi.'],
      montage: ['Montage vidéo', 'Le rythme qui retient et les accroches qui arrêtent le scroll.'],
      ia: ['IA & Automatisation', 'Faire travailler l’IA pour toi, et vendre des services IA.'],
    },

    filmPill: 'Regarde',
    filmPlay: 'Lancer la vidéo',
    verified: 'Compte vérifié',
    player: { play: 'Lecture', pause: 'Pause', back: 'Reculer de 10 secondes', fwd: 'Avancer de 10 secondes', progress: 'Progression' },
    filmTitle: 'Deux chemins **devant toi.**',
    stayKicker: 'Continuer seul',
    stayTitle: 'Le même mois',
    stay: 'Même routine, même assiette. **Dans 30 jours, tu en es au même point.**',
    goKicker: 'Commencer aujourd’hui',
    go: '**Un appel par mois, un plan, 6 campus et une communauté.** Sans engagement.',

    winsPill: 'Dans le groupe',
    winsTitle: 'Les bros **avancent.**',
    resultsNote: 'Résultats individuels, non garantis : ils dépendent de la situation et du travail de chacun. Les messages illustrés sont des mises en situation.',
    winsBody: 'Des membres qui partagent **leurs séances, leurs repas et leurs projets.** Tu y trouves un partenaire de salle, ou un associé.',
    wins: {
      yanis: 'Séance jambes et cardio avant le taf. Je suis trempé 🦵 qui vient demain 7 h ?',
      sarah: 'Chirashi du dimanche. Zéro kebab cette semaine le Goat 😅',
      adam: 'Premier plan tourné. Le bro m’a dit de couper le début. Ça tient.',
      sami: 'Deux ans que je la matais. Un bro m’a dit laquelle prendre.',
      enzo: 'Téléphone dans la cuisine, couché avant minuit. Trois semaines que je tiens. Ça paraît bête mais je suis plus le même',
      maxime: 'J’ai même trouvé quelqu’un qui m’a aidé à faire l’import, alors que je ne m’y connaissais pas.',
    },
    groupName: 'OpenBro · Les bros',

    campusPill: 'L’espace membre',
    campusTitle: 'Tout au même endroit. **Ouvert dès le paiement.**',
    campus: {
      musculation: 'Musculation & Discipline',
      reseaux: 'E-commerce & Réseaux sociaux',
      montage: 'Montage vidéo',
      ia: 'IA & Automatisation',
      nutrition: 'Nutrition & Performance',
      filming: 'Tournage & Filming',
    },
    lessons: (n) => `${n} leçons`,
    vaultLock: 'Réservé aux membres',
    vaultText: '',

    mentorPill: 'Pourquoi un appel',
    mentorTitle: 'Un regard **extérieur.**',
    mentor: [
      'Tes proches te disent que ça va. **Gaut te dit où tu en es vraiment.**',
      'Ce qui marche, ce qui te freine, **et ce que tu fais dès demain.**',
      'Le mois suivant, il te rappelle. **On voit tout de suite si c’est fait.**',
    ],
    mentorSign: 'Gaut Bonac',

    choiceTitle: 'Deux chemins. **Un seul avance.**',
    nothingTitle: 'Continuer seul',
    nothingSub: 'Le même mois, encore une fois',
    nothingCost: 'Rien ne change',
    nothing: [
      'Attendre la motivation',
      'Manger ce qui traîne',
      'T’entraîner sans plan',
      'Remettre ton projet à lundi',
      'Rester seul avec tes questions',
    ],
    nothingCta: 'Rester comme je suis',
    nothingHref: 'https://fr.wikipedia.org/wiki/Nomenclature_d%27activit%C3%A9s_fran%C3%A7aise',
    actTitle: 'OpenBro',
    actSub: 'Commencer aujourd’hui',
    perDay: (two) => `Moins de ${two} par jour.`,
    perks: [
      'Un appel privé avec Gaut chaque mois',
      'Ton plan 30 jours : alimentation, entraînement, business',
      'Les 6 campus et leurs leçons',
      'La communauté privée',
      'Le suivi de tes habitudes et le Monk Mode',
      'Sur mobile et ordinateur',
    ],
    priceFine: (price) => `${price} par mois, renouvelé automatiquement chaque mois jusqu’à résiliation. Sans engagement, résiliable à tout moment.`,
    secure: 'Paiement sécurisé par Stripe',
    crypto: 'Payer en USDC (Solana)',

    faqPill: 'FAQ',
    faqTitle: 'Questions **fréquentes**',
    faq: [
      ['Comment se passe l’appel ?', 'Tu choisis ton créneau **avant de payer**. Gaut t’appelle au numéro indiqué au paiement. Vous faites le point sur ta situation et **tu repars avec tes priorités du mois.**'],
      ['Pourquoi le créneau a une limite ?', 'Un créneau se réserve **jusqu’à 1 h 30 avant son heure.** Ensuite il disparaît et d’autres créneaux sont proposés.'],
      ['C’est pour qui ?', 'Pour toi si tu veux avancer sur **ton business, ton argent, ton corps ou ta vie perso**, et que tu es prêt à faire le travail.'],
      ['Que se passe-t-il juste après le paiement ?', 'Tu actives ton compte avec ton prénom et ton email. Tu te connectes avec un code reçu par email, et **tu as accès à tout** : campus, suivi, communauté.'],
      ['Je suis débutant, c’est un problème ?', 'Non. **Les leçons partent de zéro**, et l’appel part de **ta situation**, pas d’un programme standard.'],
      ['Combien ça coûte ?', (price) => `**${price} par mois**, prélevés par Stripe et renouvelés chaque mois jusqu’à ce que tu résilies. Tu peux aussi payer en USDC sur Solana.`],
      ['Je peux arrêter quand je veux ?', 'Oui. **L’abonnement est sans engagement**, tu le résilies quand tu veux. Les mois déjà payés ne sont pas remboursés.'],
      ['Les résultats sont-ils garantis ?', 'Oui. **Tu as un plan, un regard extérieur et un cadre.** Le travail, c’est toi qui le fais.'],
      ['Les vidéos et les messages, c’est qui ?', 'Ce sont **des mises en situation**, pas des membres filmés ni de vraies conversations. Les campus et les écrans de l’espace membre **viennent du vrai produit.**'],
    ],
    faqMore: 'Une autre question ? Écris à',

    finalTitle: 'Ton créneau **t’attend.**',
    finalPill: 'Créneaux disponibles',
    finalBro: 'Dans 30 jours, tu peux avoir **un plan tenu**, ou le même mois derrière toi. **Et pas tout seul** : dans OpenBro, tu trouves ton bro.',

    footerTag: 'by Gaut Bonac',
    terms: 'Conditions générales',
    privacy: 'Confidentialité',
    rights: 'Tous droits réservés.',
    membersWord: 'membres',
    followersLabel: 'abonnés sur TikTok et Instagram',
  },
  en: {
    lang: 'EN',
    switchLabel: 'Passer en français',
    nav: [['#chemins', 'Why'], ['#appel', 'The call'], ['#bros', 'The bros'], ['#campus', 'Member area'], ['#prix', 'Price'], ['#faq', 'FAQ']],
    login: 'Log in',
    headerLogin: 'Log in',
    headerCta: 'Join',
    eyebrow: 'Private mentorship with Gaut Bonac',
    title: 'One mentor.\nOne plan.\n**Starting today.**',
    lead: 'Every month, **Gaut calls you.** You clear what’s blocking you, and you leave with **your plan for the next 30 days.**',
    between: 'Between calls: **6 campuses** and **a private community** to stick to it.',
    cta: (price) => `Join for ${price}/month`,
    ctaShort: 'Join',
    secondary: 'See the two paths',
    reassure: ['No commitment', 'Cancel anytime', 'Instant access'],
    period: '/month',

    cardKicker: 'Your first call',
    cardTitle: 'Pick your slot.',
    cardBody: 'Gaut calls you on the number you give at checkout. The slot is yours as soon as you pay.',
    slotsLabel: 'Open slots',
    bookCta: (label) => `Book ${label}`,
    bookFallback: 'Book my call',
    cardFoot: (price) => `Included in the ${price}/month subscription`,
    toasts: [
      ({ slot }) => ['Your call', slot, 'Still open'],
      ({ price, perDay, previous, discount, saving }) => [
        'The OpenBro offer',
        `${price}/month`,
        discount ? `−${discount} · you save ${saving}/month` : `Under ${perDay} a day · no commitment`,
        previous,
      ],
      () => ['All included', '1 private call + 6 campuses', 'And the community, the moment you pay'],
    ],
    memberToast: ({ name, city }, ago) => ['Just joined', city ? `${name} · ${city}` : name, ago],
    toastClose: 'Close',

    takeSlot: (label) => `Book ${label}`,
    headerSlot: (time) => `Book ${time}`,
    barSlot: 'Your slot',
    barTake: 'Take it',
    barUntil: (left) => `Bookable for ${left} more`,
    heroUrge: ({ label, left }) => `**Your call: ${label}.** Bookable for **${left}** more. Then it passes.`,
    leftFmt: { day: 'd', hour: 'h', min: 'm', sec: 's', tight: true },
    urge: {
      hero: ({ left }) => `**Book it now.** ${left} left, then the slot passes.`,
      truth: ({ label }) => `**Your call is ready: ${label}.**`,
      stories: ({ label }) => `One call, then a plan. **Yours: ${label}.**`,
      paths: ({ left }) => `**Your slot is bookable for ${left} more.**`,
      time: ({ label }) => `**Your next slot: ${label}.**`,
      call: ({ left }) => `**Bookable for ${left} more.** Then it passes.`,
      wins: ({ label }) => `**Your slot is waiting: ${label}.**`,
      mentor: ({ label }) => `**It starts with a call.** Yours: ${label}.`,
      price: ({ left, price }) => `**${price}/month.** Your slot is bookable for ${left} more.`,
      final: ({ label, left }) => `**${label}.** Bookable for ${left} more.`,
    },

    truthPill: 'Take action',
    truthTitle: 'School never taught you **how to move forward.**',
    truth: [
      'You were taught to memorize. **Not to get up early, eat right or launch a project.**',
      'There’s no shortage of free videos. What’s missing is **someone who tells you what to do this month**, and checks on it the next.',
      '**That’s OpenBro**: one call, one plan, and a structure to stick to it.',
    ],

    storiesTitle: 'One call. **Then a plan.**',
    stories: { adam: ['The gym', '220 lb squat'], ines: ['The plate', 'Meal prep every Sunday'], theo: ['Business', 'Site live in 30 days'], lucas: ['Daily life', 'Up at 6:30'] },

    pathsPill: 'Choose your path',
    pathsTitle: 'Going alone **vs** OpenBro',
    aloneTitle: 'Keep going alone',
    alone: [
      'Hours of videos, nothing moves',
      'Advice that contradicts itself',
      'Nobody to answer you',
      'You eat whatever is around',
      'You train without a plan',
      'You start again on Monday',
    ],
    aloneCta: 'Keep going alone',
    aloneHref: 'https://en.wikipedia.org/wiki/Standard_Industrial_Classification',
    withTitle: 'OpenBro',
    with: [
      'A private call with Gaut every month',
      'Your 30-day plan: food, training, business',
      'All 6 campuses and their lessons',
      'The private community',
      'Habit tracking and Monk Mode',
      'No commitment, cancel anytime',
    ],

    timePill: 'Discipline',
    timeTitle: 'Moving forward is **a skill.**',
    time: [
      'You learn it like anything else: **a goal, steps, and someone who asks you about them.**',
      '**Thirty days is enough to change a routine.** As long as you have a plan.',
    ],
    planPill: 'Your plan',
    planTitle: 'Three plans. **One direction.**',
    plan: [
      '**Food**: what to eat, and when.',
      '**Training**: your sessions for the week, in order.',
      '**Business**: the actions that move your project forward.',
      'We build it **together, during the call.**',
    ],
    planShot: 'Real screenshot: Nutrition & Performance program',

    callPill: 'The mentor',
    callTitle: 'Gaut,\n**on the phone.**',
    callTitleSlot: (label) => `Your call.\n**${label}.**`,
    call: (minutes) => [
      `You pick your slot **before you pay**. Gaut calls you on the number you give${minutes ? `: **${minutes} min, one to one.**` : '.'}`,
      'Not a recorded class. **A conversation about your situation**, and you leave with your priorities for the month.',
    ],
    callShot: 'Real screenshot: booking on mobile',

    formationsPill: 'Access 6 campuses',
    formationsTitle: 'What you’ll **learn**',
    formationsBody: 'Practical lessons, from zero to execution. **Start with what matters to you.**',
    formations: {
      musculation: ['Strength & Discipline', 'Technique, programming, progression. Beginner to intermediate.'],
      nutrition: ['Nutrition & Performance', 'What to eat, when, and a simple system you can keep.'],
      reseaux: ['E-commerce & Social media', 'Launch your brand, sell online, grow an account.'],
      startup: ['Startup', 'Your idea finds its co-founder here. A bro to launch the project with you.'],
      montage: ['Video editing', 'Pacing that holds attention and hooks that stop the scroll.'],
      ia: ['AI & Automation', 'Put AI to work for you, and sell AI services.'],
    },

    filmPill: 'Watch',
    filmPlay: 'Play the video',
    verified: 'Verified account',
    player: { play: 'Play', pause: 'Pause', back: 'Back 10 seconds', fwd: 'Forward 10 seconds', progress: 'Progress' },
    filmTitle: 'Two paths **in front of you.**',
    stayKicker: 'Keep going alone',
    stayTitle: 'The same month',
    stay: 'Same routine, same plate. **In 30 days, you’re in the same place.**',
    goKicker: 'Start today',
    go: '**One call a month, a plan, 6 campuses and a community.** No commitment.',

    winsPill: 'In the group',
    winsTitle: 'The bros **are moving.**',
    resultsNote: 'Individual results, not guaranteed: they depend on each person’s situation and effort. Illustrated messages are dramatizations.',
    winsBody: 'Members sharing **their workouts, their meals and their projects.** You find a gym partner there, or a co-founder.',
    wins: {
      yanis: 'Legs and cardio before work. I’m soaked 🦵 who’s in tomorrow 7am?',
      sarah: 'Sunday chirashi. Zero kebab this week, the Goat 😅',
      adam: 'First video shot. A bro told me to cut the opening. It holds.',
      sami: 'Two years I’d been staring at it. A bro told me which one to get.',
      enzo: 'Phone stays in the kitchen, in bed before midnight. Three weeks now. Sounds dumb but I’m not the same guy',
      maxime: 'I even found someone who helped me handle the import, even though I knew nothing about it.',
    },
    groupName: 'OpenBro · The bros',

    campusPill: 'The member area',
    campusTitle: 'Everything in one place. **Open the moment you pay.**',
    campus: {
      musculation: 'Strength & Discipline',
      reseaux: 'E-commerce & Social media',
      montage: 'Video editing',
      ia: 'AI & Automation',
      nutrition: 'Nutrition & Performance',
      filming: 'Filming',
    },
    lessons: (n) => `${n} lessons`,
    vaultLock: 'Members only',
    vaultText: '',

    mentorPill: 'Why a call',
    mentorTitle: 'An **outside** view.',
    mentor: [
      'The people close to you say you’re fine. **Gaut tells you where you really stand.**',
      'What works, what holds you back, **and what you do starting tomorrow.**',
      'The next month, he calls again. **You see right away if it’s done.**',
    ],
    mentorSign: 'Gaut Bonac',

    choiceTitle: 'Two paths. **Only one moves.**',
    nothingTitle: 'Keep going alone',
    nothingSub: 'The same month, once again',
    nothingCost: 'Nothing changes',
    nothing: [
      'Wait for motivation',
      'Eat whatever is around',
      'Train without a plan',
      'Push your project to Monday',
      'Stay alone with your questions',
    ],
    nothingCta: 'Stay as I am',
    nothingHref: 'https://en.wikipedia.org/wiki/Standard_Industrial_Classification',
    actTitle: 'OpenBro',
    actSub: 'Start today',
    perDay: (two) => `Less than ${two} a day.`,
    perks: [
      'A private call with Gaut every month',
      'Your 30-day plan: food, training, business',
      'All 6 campuses and their lessons',
      'The private community',
      'Habit tracking and Monk Mode',
      'On phone and computer',
    ],
    priceFine: (price) => `${price} per month, renews automatically every month until you cancel. No commitment, cancel anytime.`,
    secure: 'Secure payment by Stripe',
    crypto: 'Pay in USDC (Solana)',

    faqPill: 'FAQ',
    faqTitle: 'Common **questions**',
    faq: [
      ['How does the call work?', 'You pick your slot **before paying**. Gaut calls you on the number you give at checkout. You go over your situation and **you leave with your priorities for the month.**'],
      ['Why does the slot have a deadline?', 'A slot can be booked **until 1h30 before its time.** After that it’s gone and other slots are offered.'],
      ['Who is it for?', 'For you if you want to move forward on **your business, money, body or personal life**, and you’re ready to do the work.'],
      ['What happens right after I pay?', 'You activate your account with your first name and email. You log in with a code sent by email, and **you get access to everything**: campuses, tracking, community.'],
      ['I’m a beginner. Is that a problem?', 'No. **The lessons start from zero**, and the call starts from **your situation**, not a standard plan.'],
      ['How much is it?', (price) => `**${price} a month**, billed by Stripe and renewed every month until you cancel. You can also pay in USDC on Solana.`],
      ['Can I stop whenever I want?', 'Yes. **No commitment**: email us and we cancel your renewal, effective at the end of the month you paid for. Months already paid are not refunded.'],
      ['Are results guaranteed?', 'Yes. **You get a plan, an outside view and structure.** The work is yours to do.'],
      ['Who are the people in the videos and messages?', 'They are **dramatizations**, not filmed members or real conversations. The campuses and member-area screens **come from the real product.**'],
    ],
    faqMore: 'Another question? Email',

    finalTitle: 'Your slot **is waiting.**',
    finalPill: 'Open slots',
    finalBro: 'In 30 days, you can have **a plan you kept**, or the same month behind you. **And not alone**: in OpenBro, you find your bro.',

    footerTag: 'by Gaut Bonac',
    terms: 'Terms',
    privacy: 'Privacy',
    rights: 'All rights reserved.',
    membersWord: 'members',
    followersLabel: 'followers on TikTok and Instagram',
  },
};

export default COPY;
